/**
 * Ticket detail APIs (`viewTicketsGuest` / `viewTicketsWithTicketId`): success body
 * `data` is **TicketRow[]** — use `data.length` for offline bundles; render each row (QR, id, type).
 * Multi-pass units expose passQrcodes[] / qrcodeimages[] — expanded to one row per pass.
 */
import { collectTicketPassQrs } from "../../helpers/offlineSaleResponse";

/** Guest secret — 64 hex (legacy) or other opaque tokens (SMS / deep links). Do not log tokens. */
export const GUEST_TOKEN_HEX_64 = /^[a-fA-F0-9]{64}$/;
const GUEST_TOKEN_JWT_LIKE =
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/**
 * Legacy only: parallel `qrcodes[]` on one object. Bundle row count comes from
 * ticket-detail `data.length`, not `noofticket`.
 */
export function fanOutConsolidatedTickets(block, tickets) {
  const list = Array.isArray(tickets) ? tickets.filter(Boolean) : [];
  if (Array.isArray(block)) return list;
  if (!block || typeof block !== "object") {
    return list.length ? list : [];
  }

  if (list.length === 0) {
    const orphanQrs = collectParallelQrStrings(block);
    if (orphanQrs.length >= 2) {
      const orphanIds = collectParallelTicketIds(block);
      return orphanQrs.map((qr, i) => ({
        ...block,
        qrcode: qr,
        qrCode: qr,
        qr_image: qr,
        ticketid:
          orphanIds[i] ??
          block.ticketid ??
          block.ticketId ??
          undefined,
        ticketId: orphanIds[i] ?? block.ticketId,
      }));
    }
    return [];
  }

  if (list.length > 1) return list;

  const base =
    list.length === 1 && typeof list[0] === "object" ? { ...list[0] } : {};

  const qrsPrimary =
    collectParallelQrStrings(base).length >= 2
      ? collectParallelQrStrings(base)
      : collectParallelQrStrings(block);

  const idsPrimary =
    collectParallelTicketIds(base).length >= 2
      ? collectParallelTicketIds(base)
      : collectParallelTicketIds(block);

  if (qrsPrimary.length >= 2) {
    return qrsPrimary.map((qr, i) => ({
      ...base,
      qrcode: qr,
      qrCode: qr,
      qr_image: qr,
      qrImage: qr,
      ticketid:
        idsPrimary[i] ??
        base.ticketid ??
        base.ticketId ??
        base.ticketcode ??
        base.code,
      ticketId: idsPrimary[i] ?? base.ticketId ?? base.ticketid,
    }));
  }

  return list.length ? list : [];
}

function collectParallelQrStrings(obj) {
  if (!obj || typeof obj !== "object") return [];

  const fromPasses = collectTicketPassQrs(obj);
  if (fromPasses.length >= 2) {
    return fromPasses.map((p) => p.qrcode);
  }

  const keys = [
    "qrcodes",
    "ticketQrCodes",
    "qrCodes",
    "qr_images",
    "qrImages",
    "qrs",
    "qrCodeList",
    "qrcodeimages",
    "qrCodeImages",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (!Array.isArray(v)) continue;
    const mapped = v
      .map((x) => {
        if (x == null) return "";
        if (typeof x === "string") return x.trim();
        if (typeof x === "object") {
          return String(
            x.qrcodeimage ?? x.qrCodeImage ?? x.qrcode ?? x.qrCode ?? x.url ?? ""
          ).trim();
        }
        return String(x).trim();
      })
      .filter(Boolean);
    if (mapped.length >= 2) return mapped;
  }
  return [];
}

/** One display row per entry pass when passQrcodes / qrcodeimages are present. */
export function expandTicketsByPassQrs(tickets) {
  const list = Array.isArray(tickets) ? tickets.filter(Boolean) : [];
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const t = list[i];
    if (!t || typeof t !== "object") continue;
    if (t._passExpanded === true || (t.passIndex != null && t.passCount != null)) {
      out.push(t);
      continue;
    }
    const passes = collectTicketPassQrs(t);
    if (passes.length <= 1) {
      out.push(t);
      continue;
    }
    for (let p = 0; p < passes.length; p += 1) {
      const pass = passes[p];
      out.push({
        ...t,
        passQrcodes: undefined,
        passQrCodes: undefined,
        qrcodeimages: undefined,
        qrCodeImages: undefined,
        qrcode: pass.qrcode,
        qrCode: pass.qrcode,
        qrcodeimage: pass.qrcode,
        qrCodeImage: pass.qrcode,
        passIndex: pass.passIndex,
        passCount: passes.length,
        ticketid: pass.ticketId ?? t.ticketid ?? t.ticketId,
        ticketId: pass.ticketId ?? t.ticketId ?? t.ticketid,
        _passKey: `${i}-${pass.key}`,
        _passUnitIndex: i,
        _passExpanded: true,
      });
    }
  }
  return out;
}

function collectParallelTicketIds(obj) {
  if (!obj || typeof obj !== "object") return [];
  const keys = [
    "ticketids",
    "ticketIds",
    "ticketCodes",
    "ticketcodes",
    "ticket_id",
    "ticket_id_list",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (!Array.isArray(v)) continue;
    const mapped = v.map((x) =>
      x != null && String(x) !== "" ? String(x) : ""
    );
    if (mapped.some(Boolean)) return mapped;
  }
  return [];
}

function pickEventTitleFromRows(blockOrTicketList, fallbackFirstRow) {
  const row =
    fallbackFirstRow ??
    (Array.isArray(blockOrTicketList)
      ? blockOrTicketList[0]
      : blockOrTicketList);
  const fromBlock =
    blockOrTicketList &&
    typeof blockOrTicketList === "object" &&
    !Array.isArray(blockOrTicketList)
      ? blockOrTicketList.eventTitle ??
        blockOrTicketList.event?.title ??
        blockOrTicketList.eventDetails?.title ??
        blockOrTicketList.eventname
      : null;
  const fromRow =
    row && typeof row === "object"
      ? row.eventTitle ??
        row.event?.title ??
        row.eventDetails?.title ??
        row.title ??
        row.eventname
      : "";
  const title = fromBlock ?? fromRow ?? "";
  return title ? String(title) : "";
}

/**
 * Maps GET viewTicketsGuest / viewTicketsWithTicketId bodies to `{ tickets, eventTitle }`.
 * Primary contract: success `data` is **TicketRow[]** — render `data.length` rows, each with its own QR fields.
 */
export function normalizeTicketsView(raw) {
  const root = raw?.data != null ? raw.data : raw;
  const block =
    root && typeof root === "object" && !Array.isArray(root) && root.data != null
      ? root.data
      : root;

  if (Array.isArray(block)) {
    const tickets = expandTicketsByPassQrs(block.filter((x) => x != null));
    return {
      eventTitle: pickEventTitleFromRows(block, tickets[0]),
      tickets,
    };
  }

  let tickets = [];
  if (block && typeof block === "object") {
    if (Array.isArray(block.ticketDetails) && block.ticketDetails.length)
      tickets = block.ticketDetails;
    else if (
      Array.isArray(block.generatedTickets) &&
      block.generatedTickets.length
    )
      tickets = block.generatedTickets;
    else if (
      Array.isArray(block.individualTickets) &&
      block.individualTickets.length
    )
      tickets = block.individualTickets;
    else if (

      Array.isArray(block.order?.tickets) &&
      block.order.tickets.length
    )
      tickets = block.order.tickets;
    else if (Array.isArray(block.tickets)) tickets = block.tickets;
    else if (Array.isArray(block.ticket)) tickets = block.ticket;
    else if (Array.isArray(block.ticketList)) tickets = block.ticketList;
    else if (Array.isArray(block.items)) tickets = block.items;
    else if (Array.isArray(block.rows)) tickets = block.rows;
    else if (Array.isArray(block.orders)) tickets = block.orders;
    else if (Array.isArray(block.orderTickets)) tickets = block.orderTickets;
    else if (Array.isArray(block.results)) tickets = block.results;
    else if (block.ticket && typeof block.ticket === "object") {
      tickets = [block.ticket];
    }
    if (
      tickets.length === 0 &&
      (block.ticketid != null ||
        block.ticketId != null ||
        block.qrcode != null ||
        block.qrCode != null ||
        block.qrcodeimage != null ||
        (Array.isArray(block.passQrcodes) && block.passQrcodes.length) ||
        (Array.isArray(block.qrcodeimages) && block.qrcodeimages.length))
    ) {
      tickets = [{ ...block }];
    }
  }

  tickets = fanOutConsolidatedTickets(block, tickets);
  tickets = expandTicketsByPassQrs(tickets);

  const eventTitle = pickEventTitleFromRows(block, tickets[0]);

  return { eventTitle, tickets };
}

/** Prefer booking ref (public) over internal ticket id. */
export function pickBookingRefLabel(t) {
  if (!t || typeof t !== "object") return "";
  const ref =
    t.bookingref ??
    t.bookingRef ??
    t.booking_ref ??
    t.bookingReference ??
    t.booking_reference ??
    "";
  return ref != null && String(ref).trim() !== "" ? String(ref).trim() : "";
}

/** Single booking ref for the order summary. */
export function pickBookingRefSummary(tickets) {
  if (!tickets?.length) return "";
  for (let i = 0; i < tickets.length; i += 1) {
    const ref = pickBookingRefLabel(tickets[i]);
    if (ref) return ref;
  }
  const t0 = tickets[0];
  const fallback =
    t0?.code ??
    t0?.ticketCode ??
    t0?.ticketcode ??
    "";
  return fallback != null && String(fallback).trim() !== ""
    ? String(fallback).trim()
    : "";
}

export function pickTicketLabel(t, i) {
  const passIndex = Number(t?.passIndex);
  const passCount = Number(t?.passCount);
  if (
    Number.isFinite(passIndex) &&
    passIndex > 0 &&
    Number.isFinite(passCount) &&
    passCount > 1
  ) {
    return `Pass ${passIndex}`;
  }
  const id =
    t?.ticketId ??
    t?.ticketid ??
    t?._id ??
    t?.id ??
    t?.code ??
    t?.ticketCode;
  if (id != null && id !== "") return String(id);
  return `#${i + 1}`;
}

export function pickTicketQrSrc(t) {
  if (!t || typeof t !== "object") return "";
  const passes = collectTicketPassQrs(t);
  if (passes.length > 0) {
    const idx = Number(t.passIndex);
    if (Number.isFinite(idx) && idx > 0) {
      const match = passes.find((p) => p.passIndex === idx);
      if (match?.qrcode) return match.qrcode;
    }
    if (passes.length === 1 || t.passIndex != null) {
      return passes[0]?.qrcode ?? "";
    }
    return passes[0]?.qrcode ?? "";
  }
  const v =
    t?.qrcode ??
    t?.qrCode ??
    t?.qrcodeimage ??
    t?.qrCodeImage ??
    t?.qr ??
    t?.qrUrl ??
    t?.qr_image ??
    t?.qrImage ??
    t?.barcodeImage ??
    t?.barcode ??
    t?.barcodeUrl ??
    t?.image;
  if (v == null || v === "") return "";
  return String(v);
}

/** SMS / mobile browsers may receive raw base64 without data URI — make <img src> safe. */
export function normalizeTicketQrImgSrc(src) {
  if (!src || typeof src !== "string") return "";
  const s = src.trim();
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:") ||
    s.startsWith("/")
  ) {
    return s;
  }
  return `data:image/jpeg;base64,${s}`;
}

export function pickTicketTypeLabel(t) {
  const v =
    t?.ticketType ??
    t?.tickettype ??
    t?.ticketCategory ??
    t?.categoryName;
  return v != null && v !== "" ? String(v) : "";
}

/** Venue / location line for M-ticket header (from nested `event` on ticket rows). */
export function pickEventVenue(tickets) {
  const t0 = tickets?.[0];
  if (!t0 || typeof t0 !== "object") return "";
  const e = t0.event || t0.eventDetails || t0.eventInfo;
  const v =
    e?.location ??
    e?.venue ??
    e?.address ??
    t0.location ??
    t0.venue ??
    t0.eventAddress ??
    "";
  return typeof v === "string" && v.trim() !== "" ? v.trim() : "";
}

/** Event poster / banner URL when API embeds event on ticket row (https, data, or absolute path). */
export function pickEventImageSrc(tickets) {
  const t0 = tickets?.[0];
  if (!t0 || typeof t0 !== "object") return "";
  const e = t0.event || t0.eventDetails || t0.eventInfo;
  const raw =
    e?.image ??
    e?.banner ??
    e?.coverImage ??
    e?.coverimage ??
    e?.poster ??
    t0.eventImage ??
    t0.eventimage ??
    "";
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:") ||
    s.startsWith("/")
  ) {
    return s;
  }
  return "";
}

/**
 * ISO date string or Date for displaying event start (first row).
 * @returns {string|Date|null}
 */
export function pickEventStartRaw(tickets) {
  const t0 = tickets?.[0];
  if (!t0 || typeof t0 !== "object") return null;
  const e = t0.event || t0.eventDetails || t0.eventInfo;
  const raw =
    e?.startdatetime ??
    e?.startDateTime ??
    e?.start_datetime ??
    t0.startdatetime ??
    t0.eventDate ??
    t0.eventdate ??
    null;
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) return raw;
  const s = String(raw).trim();
  return s || null;
}

/**
 * Human-readable quantity × category line (e.g. "2 × VIP").
 * @param {{ length: number }} tickets
 * @param {(key: string, opts?: object) => string} [t] i18n `t` for multi-ticket fallback
 */
export function pickQuantityTypeLine(tickets, t) {
  if (!tickets?.length) return "";
  const t0 = tickets[0];
  const perOrder = Number(t0?.noofticket ?? 0);
  const typ = pickTicketTypeLabel(t0);

  const isMultiPassOneUnit =
    tickets.length > 1 &&
    tickets.every(
      (r) =>
        r?._passExpanded === true &&
        Number(r?.passCount) === Number(t0?.passCount) &&
        Number(t0?.passCount) > 1
    );

  if (isMultiPassOneUnit || tickets.length === 1) {
    const n = perOrder > 0 ? perOrder : 1;
    if (typ) return `${n} × ${typ}`;
    return n > 1 ? String(n) : "";
  }

  const types = tickets.map((row) => pickTicketTypeLabel(row)).filter(Boolean);
  if (types.length === tickets.length && new Set(types).size === 1) {
    return `${tickets.length} × ${types[0]}`;
  }
  return typeof t === "function"
    ? t("MY_TICKETS_MULTI_COUNT", { n: tickets.length })
    : `${tickets.length} tickets`;
}

/**
 * Group flat ticket rows for Entry QR UI: category → ticket unit → guest passes.
 * @returns {Array<{
 *   categoryKey: string,
 *   categoryLabel: string,
 *   totalPasses: number,
 *   units: Array<{
 *     unitKey: string,
 *     unitIndex: number,
 *     guests: Array<{ row: object, guestIndex: number, guestCount: number, flatKey: string }>,
 *   }>,
 * }>}
 */
export function groupTicketsForEntryQrDisplay(tickets) {
  const rows = Array.isArray(tickets) ? tickets.filter(Boolean) : [];
  if (!rows.length) return [];

  const categoryMap = new Map();

  rows.forEach((row, rowIndex) => {
    const type = pickTicketTypeLabel(row) || "Ticket";
    const categoryKey = type.toLowerCase().trim() || "ticket";
    const bookingRef = pickBookingRefLabel(row);
    let unitKey;
    if (row._passUnitIndex != null) {
      unitKey = String(row._passUnitIndex);
    } else if (bookingRef) {
      const purchaseUnit =
        row.purchaseUnitIndex ??
        row.ticketUnitIndex ??
        row.bundleIndex ??
        (Number(row.noofticket) > 1 ? rowIndex : 0);
      unitKey = `ref-${bookingRef}-u${purchaseUnit}`;
    } else {
      unitKey = String(
        row._id ?? row.ticketid ?? row.ticketId ?? rowIndex
      );
    }

    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, {
        categoryKey,
        categoryLabel: type,
        units: new Map(),
      });
    }
    const cat = categoryMap.get(categoryKey);

    if (!cat.units.has(unitKey)) {
      cat.units.set(unitKey, {
        unitKey,
        unitIndex: cat.units.size + 1,
        guests: [],
      });
    }

    const unit = cat.units.get(unitKey);
    const passIndex = Number(row.passIndex);
    const passCount = Number(row.passCount);
    const isMultiPass =
      row._passExpanded === true &&
      Number.isFinite(passIndex) &&
      passIndex > 0 &&
      Number.isFinite(passCount) &&
      passCount > 1;

    unit.guests.push({
      row,
      guestIndex: isMultiPass ? passIndex : unit.guests.length + 1,
      guestCount: isMultiPass ? passCount : 0,
      flatKey:
        row._passKey ??
        `${categoryKey}-${unitKey}-${unit.guests.length}`,
    });
  });

  return Array.from(categoryMap.values()).map((cat) => {
    const units = Array.from(cat.units.values()).map((unit) => {
      const guestCount =
        unit.guests.length === 1 && unit.guests[0].guestCount <= 1
          ? 1
          : unit.guests[0]?.guestCount > 1
            ? unit.guests[0].guestCount
            : unit.guests.length;
      const guests = unit.guests.map((g, i) => ({
        ...g,
        guestIndex:
          g.guestCount > 1 && g.guestIndex > 0 ? g.guestIndex : i + 1,
        guestCount,
      }));
      return { ...unit, guests };
    });
    const totalPasses = units.reduce((sum, u) => sum + u.guests.length, 0);
    return {
      categoryKey: cat.categoryKey,
      categoryLabel: cat.categoryLabel,
      totalPasses,
      units,
    };
  });
}

/** Comma-separated ticket ids / labels for summary line. */
export function pickTicketIdsSummary(tickets) {
  if (!tickets?.length) return "";
  const parts = tickets.map((row, i) => pickTicketLabel(row, i));
  return parts.filter(Boolean).join(", ");
}

function decodeQueryToken(raw) {
  if (raw == null || raw === "") return "";
  try {
    return decodeURIComponent(String(raw).trim());
  } catch {
    return String(raw).trim();
  }
}

/**
 * Reads guest token from query (SMS links). Supports multiple param names and
 * hex64 / JWT-like / other opaque secrets — backend validates server-side.
 */
export function guestTokenFromSearchParams(searchParams) {
  if (!searchParams?.get) return "";
  const keys = ["token", "access_token", "t", "guestToken", "key"];
  for (let i = 0; i < keys.length; i += 1) {
    const raw = searchParams.get(keys[i]);
    const tokenParam = decodeQueryToken(raw);
    if (!tokenParam) continue;
    if (GUEST_TOKEN_HEX_64.test(tokenParam)) return tokenParam;
    if (GUEST_TOKEN_JWT_LIKE.test(tokenParam)) return tokenParam;
    if (
      tokenParam.length >= 12 &&
      /^[A-Za-z0-9._~+-]+$/.test(tokenParam)
    )
      return tokenParam;
  }
  return "";
}

/** Token may live in hash (#token=...) when carriers rewrite query strings. */
export function guestTokenFromWindowLocation() {
  if (typeof window === "undefined") return "";
  const fromQuery = guestTokenFromSearchParams(
    new URLSearchParams(window.location.search || "")
  );
  if (fromQuery) return fromQuery;
  const hash = window.location.hash || "";
  if (!hash.startsWith("#")) return "";
  try {
    const withoutHash = hash.slice(1);
    const queryPart = withoutHash.includes("?")
      ? withoutHash.slice(withoutHash.indexOf("?") + 1)
      : withoutHash.includes("=")
        ? withoutHash
        : "";
    if (!queryPart) return "";
    return guestTokenFromSearchParams(new URLSearchParams(queryPart));
  } catch {
    return "";
  }
}
